import { Game } from "@aura-frontend/omnibus-az-client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  formatDate,
  generateTimeOptions,
  Label,
  Separator,
  TimePicker,
  useConfirmStore,
} from "@win-reality/react-ui";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { formatTo12Hour } from "@/utils/dateUtils";

import {
  overviewFormSchema,
  type OverviewFormValues,
} from "../EventForm.schema";
import { LiveEventLockedAlert } from "../LiveEventLockedAlert/LiveEventLockedAlert";
import { TimeSlotAvailability } from "../TimeSlotAvailability/TimeSlotAvailability";
import {
  buildEventFormDefaultValues,
  EVENT_TYPE_OPTIONS,
} from "./Overview.utils";

type OverviewLiveProps = {
  onValidChange: (isValid: boolean) => void;
  fieldId: string;
  event?: Game;
  initialName?: string | null;
  initialNotes?: string | null;
  excludeScheduleId?: string;
  onEndTimeUpdate: (endTime: string) => void;
  onEndEvent?: () => void | Promise<void>;
  isUpdating?: boolean;
};

function OverviewLive({
  onValidChange,
  fieldId,
  event,
  initialName,
  initialNotes,
  excludeScheduleId,
  onEndTimeUpdate,
  onEndEvent,
  isUpdating,
}: OverviewLiveProps) {
  const formValues = useMemo(
    () =>
      buildEventFormDefaultValues(event, fieldId, initialName, initialNotes),
    [event, fieldId, initialName, initialNotes],
  );

  const form = useForm<OverviewFormValues>({
    resolver: zodResolver(overviewFormSchema),
    mode: "onChange",
    defaultValues: formValues,
  });

  const [isSlotAvailable, setIsSlotAvailable] = useState(true);

  const confirm = useConfirmStore((s) => s.confirm);

  const handleEndEventClick = async () => {
    if (!onEndEvent) return;
    const confirmed = await confirm({
      title: "End Event?",
      description:
        "Ending this event will stop tracking and close the session. This action cannot be undone.",
      confirmText: "End Event",
      confirmButtonVariant: "destructive",
    });
    if (!confirmed) return;
    useConfirmStore.setState({ isPending: true });
    try {
      await onEndEvent();
    } finally {
      useConfirmStore.setState({ isOpen: false, isPending: false });
    }
  };

  const isValid = form.formState.isValid;

  const endTime = useWatch({ control: form.control, name: "endTime" });
  const startTime = useWatch({ control: form.control, name: "startTime" });
  const startDate = useWatch({ control: form.control, name: "startDate" });

  const endTimeOptions = useMemo(() => {
    const options = generateTimeOptions("15m");
    if (!startTime) return options;
    return options.filter((opt) => opt.value > startTime);
  }, [startTime]);

  const endTimeChanged = endTime !== formValues.endTime;

  useEffect(() => {
    onValidChange(isValid && (!endTimeChanged || isSlotAvailable));
  }, [isValid, endTimeChanged, isSlotAvailable, onValidChange]);

  const handleSubmit = (data: OverviewFormValues) => {
    onEndTimeUpdate(data.endTime);
  };

  const values = form.getValues();
  const { field, type, name, additionalNotes } = values;

  return (
    <div className="w-full">
      <LiveEventLockedAlert />

      <div className="text-base font-bold mb-2">Event</div>

      <div className="flex flex-col gap-4">
        <div>
          <Label className="text-sm mb-1">Field</Label>

          <div className="text-md">{field}</div>
        </div>

        <div>
          <Label className="text-sm mb-1">Type</Label>

          <div className="text-md">
            {EVENT_TYPE_OPTIONS.find((option) => option.value === type)?.label}
          </div>
        </div>

        {(event?.generated_display_name || event?.displayName || name) && (
          <div>
            <Label className="text-sm mb-1">Name</Label>

            <div className="text-md">
              {event?.generated_display_name || event?.displayName || name}
            </div>
          </div>
        )}

        <div>
          <Label className="text-sm mb-1">Start Date</Label>

          <div className="text-md">
            {startDate && !isNaN(startDate.getTime())
              ? formatDate(startDate, "MMM d, yyyy")
              : "—"}
          </div>
        </div>

        <div>
          <Label className="text-sm mb-1">Start Time</Label>

          <div className="text-md">
            {startTime ? formatTo12Hour(startTime) : "—"}
          </div>
        </div>

        <div>
          <Label className="text-sm mb-1">Additional Notes</Label>

          <div className="text-md">
            {additionalNotes && additionalNotes.length > 0
              ? additionalNotes
              : "n/a"}
          </div>
        </div>
      </div>

      <Separator className="my-6" />

      <div className="text-base font-bold mb-4"> Update Event End Time?</div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid gap-6 pb-4"
        >
          <div className="flex justify-between items-end">
            <div className="flex gap-2 items-end">
              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <Label className="text-sm mb-1" isRequired>
                      End Time
                    </Label>
                    <div className="w-40">
                      <TimePicker
                        value={field.value || undefined}
                        onValueChange={field.onChange}
                        options={endTimeOptions}
                        placeholder="Select time"
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                variant="secondary-flat-light-outline"
                isDisabled={!isValid || (endTimeChanged && !isSlotAvailable)}
                isLoading={isUpdating}
              >
                Update
              </Button>
            </div>

            <Button
              variant="destructive-outline"
              type="button"
              onClick={handleEndEventClick}
            >
              End Event
            </Button>
          </div>
        </form>

        {endTimeChanged && (
          <TimeSlotAvailability
            fieldId={fieldId}
            startTime={startTime}
            endTime={endTime}
            startDate={startDate}
            onAvailabilityChange={setIsSlotAvailable}
            excludeScheduleId={excludeScheduleId}
          />
        )}
      </Form>
    </div>
  );
}

export { OverviewLive };
